import React from 'react';

import styled, {css} from 'styled-components';

import {Stack} from '../primitives';
import {TTheme, mixins} from '../styles';
import {ESwitchSize, TSwitchSize} from './types';

type TSwitchProps = TTheme &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
    checked?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    size?: TSwitchSize;
  };

type TControlProps = TTheme & {
  checked?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  size?: TSwitchSize;
};

type TLabelProps = TTheme & {
  disabled?: boolean;
  readOnly?: boolean;
  size?: TSwitchSize;
};

const getDisabledStyle = ({disabled}: TSwitchProps) => {
  const cursor = disabled ? 'not-allowed' : 'pointer';

  return css`
    cursor: ${cursor};
  `;
};

const getSwitchControlSizeStyle = ({size}: TControlProps) => {
  switch (size) {
    case ESwitchSize.S: {
      return css`
        ${mixins.wh('28px', '16px')};
      `;
    }

    case ESwitchSize.L: {
      return css`
        ${mixins.wh('44px', '24px')};
      `;
    }

    default:

    case ESwitchSize.M: {
      return css`
        ${mixins.wh('36px', '20px')};
      `;
    }
  }
};

const getSwitchControlAfterSizeStyle = ({size, checked}: TControlProps) => {
  switch (size) {
    case ESwitchSize.S: {
      return css`
        ${checked ? mixins.wh('12px', '12px') : mixins.wh('10px', '10px')};
      `;
    }

    case ESwitchSize.L: {
      return css`
        ${checked ? mixins.wh('20px', '20px') : mixins.wh('16px', '16px')};
      `;
    }

    default:

    case ESwitchSize.M: {
      return css`
        ${checked ? mixins.wh('16px', '16px') : mixins.wh('12px', '12px')};
      `;
    }
  }
};

const getLabelSizeStyle = ({size}: TLabelProps) => {
  switch (size) {
    case ESwitchSize.S: {
      return css`
        ${mixins.f2};
      `;
    }

    case ESwitchSize.L: {
      return css`
        ${mixins.f4};
      `;
    }

    default:

    case ESwitchSize.M: {
      return css`
        ${mixins.f3};
      `;
    }
  }
};

const getSwitchSizeStyle = ({size, theme}: TSwitchProps) => {
  switch (size) {
    case ESwitchSize.S: {
      return css`
        gap: ${theme.spacing.s2};
      `;
    }
    case ESwitchSize.L:
    case ESwitchSize.M:

    default: {
      return css`
        gap: ${theme.spacing.s3};
      `;
    }
  }
};

const getDescriptionSizeStyle = ({size}: TLabelProps) => {
  switch (size) {
    case ESwitchSize.S: {
      return css`
        ${mixins.f1};
      `;
    }

    case ESwitchSize.L: {
      return css`
        ${mixins.f3};
      `;
    }

    default:

    case ESwitchSize.M: {
      return css`
        ${mixins.f2};
      `;
    }
  }
};

export const Switch = styled.label<TSwitchProps>`
  /* for HiddenInput */
  position: relative;
  display: flex;
  flex-wrap: wrap;
  ${getSwitchSizeStyle};
  align-items: center;
  transition: color ${({theme}) => theme.animation.duration}ms;
  ${getDisabledStyle}
`;

const getControlStyle = ({
  checked,
  disabled,
  readOnly,
  size,
  theme: {animation, palette, branding},
}: TControlProps) => {
  const isDisabled = disabled || readOnly;
  const borderWidth = size === ESwitchSize.S ? '1.6px' : '2px';

  if (checked) {
    return css`
      color: ${palette.white};
      transition: all ${animation.duration}ms;
      background-color: ${isDisabled ? palette.grey300 : palette.black};
      border: ${borderWidth} solid ${isDisabled ? palette.grey300 : palette.black};
    `;
  }

  return css`
    color: ${palette.white};
    transition: all ${animation.duration}ms;
    background-color: ${palette.white};
    border: ${borderWidth} solid ${isDisabled ? palette.grey300 : branding.content.secondary};
  `;
};

const getControlAfterStyle = ({checked, size, theme, disabled, readOnly}: TControlProps) => {
  if (checked) {
    return css`
      left: 100%;
      transform: translate(-100%, -50%);
      background-color: ${theme.palette.white};
    `;
  }

  return css`
    left: ${size === ESwitchSize.S ? '1.4px' : '2px'};
    transform: translate(0, -50%);
    background-color: ${disabled || readOnly
      ? theme.palette.grey300
      : theme.branding.content.secondary};
  `;
};

export const Control = styled.span<TControlProps>`
  ${mixins.f2}
  position: relative;
  display: flex;
  align-items: center;
  border-radius: ${({theme}) => theme.borderRadius.br5};
  ${getControlStyle}
  ${getSwitchControlSizeStyle}
  &::after {
    content: '';
    ${mixins.position({position: 'absolute', top: '50%'})}
    border-radius: 50%;
    background-color: currentColor;
    transition: all ${({theme}) => theme.animation.duration}ms;
    ${getControlAfterStyle}
    ${getSwitchControlAfterSizeStyle}
  }
`;

export const Label = styled.span<TLabelProps>`
  color: ${({theme, disabled, readOnly}) =>
    disabled || readOnly ? theme.palette.grey400 : theme.branding.content.secondary};
  ${getDisabledStyle}
  ${getLabelSizeStyle}
`;

export const Description = styled.span<TLabelProps>`
  color: ${({theme, disabled, readOnly}) =>
    disabled || readOnly ? theme.palette.grey400 : theme.branding.content.secondary};
  ${getDisabledStyle}
  ${getDescriptionSizeStyle}
`;

export const Wrapper = styled(Stack)`
  display: inline-flex;
`;
